import type { MaquinariaDeInforme, Moneda } from "@/lib/informes";

/* ---------------------------------------------------------------------------
   El cuadro de maquinaria: lo que se DERIVA de las filas del informe.

   Es lógica PURA y vive fuera del componente por la razón de siempre: qué se
   ve bajo un filtro o cuánto suma un total son las cuentas que pueden estar
   mal sin que nada se vea roto, y dentro del árbol un test no distingue una
   cifra buena de una cercana. Es la gemela de `cuadroDeConstrucciones.ts`
   sin lo de terreno, y sin orden ni paginación: el diseño no los trae.
--------------------------------------------------------------------------- */

export type FiltroDelCuadro = "todas" | "sin_valorizar" | "sin_validar";

/** Lo que suman las filas de UNA moneda. */
export interface TotalPorMoneda {
  /** `null` solo si una fila trae valor y no trae moneda: se suma aparte. */
  moneda: Moneda | null;
  total: number;
}

export interface ResumenDelCuadro {
  cantidad: number;
  validadas: number;
  /** Las que no tienen valor comercial: les falta el valor unitario residual. */
  sinValorizar: number;
  totales: TotalPorMoneda[];
}

export interface VistaDelCuadro {
  visibles: MaquinariaDeInforme[];
  /** Siempre sobre el conjunto entero: un chip no se encoge con la búsqueda. */
  conteos: Record<FiltroDelCuadro, number>;
  resumen: ResumenDelCuadro;
}

/**
 * Cuenta las filas y suma el valor comercial POR MONEDA.
 *
 * NUNCA SE SUMAN MONEDAS DISTINTAS: sin una tasa, "US$ 27.260 + UF 100" no es
 * ninguna cifra, y la pestaña ya declaró que no convierte (CLAUDE.md). Cada
 * moneda va en su propio total, en el orden en que aparece por primera vez.
 *
 * La suma va en CENTÉSIMAS ENTERAS: en punto flotante `0.1 + 0.2` no da `0.3`,
 * y el total mostraría una diferencia que no existe. Una fila sin valor
 * comercial no aporta, y no crea un total en cero.
 */
export function resumenDelCuadro(filas: MaquinariaDeInforme[]): ResumenDelCuadro {
  const centesimas = new Map<Moneda | null, number>();

  for (const fila of filas) {
    if (fila.valor_comercial_total === null) continue;

    centesimas.set(
      fila.moneda,
      (centesimas.get(fila.moneda) ?? 0) + Math.round(fila.valor_comercial_total * 100),
    );
  }

  return {
    cantidad: filas.length,
    validadas: filas.filter((f) => f.validada).length,
    sinValorizar: filas.filter((f) => f.valor_comercial_total === null).length,
    totales: [...centesimas].map(([moneda, suma]) => ({ moneda, total: suma / 100 })),
  };
}

/** "4 máquinas y equipos"; en singular, "1 máquina o equipo". */
export function tituloDeLaLista(cantidad: number): string {
  if (cantidad === 0) return "Máquinas y equipos";

  return cantidad === 1 ? "1 máquina o equipo" : `${cantidad} máquinas y equipos`;
}

/** "2 sin valorizar", "Todas valorizadas", o la invitación a cargar la primera. */
export function textoDelResumen(resumen: ResumenDelCuadro): string {
  if (resumen.cantidad === 0) return "Agrega la primera máquina o usa Rellenar con IA.";

  return resumen.sinValorizar > 0 ? `${resumen.sinValorizar} sin valorizar` : "Todas valorizadas";
}

/** Minúsculas y sin tildes: "NANDU" encuentra "Ñandú". */
function normalizar(texto: string): string {
  return texto
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .toLowerCase();
}

/**
 * Lo que la lista dibuja bajo la búsqueda y el filtro elegidos.
 *
 * Las filas salen en el orden en que se cargaron: el diseño no ofrece otro.
 */
export function vistaDelCuadro(
  filas: MaquinariaDeInforme[],
  { busqueda, filtro }: { busqueda: string; filtro: FiltroDelCuadro },
): VistaDelCuadro {
  const consulta = normalizar(busqueda.trim());

  const cumple = (fila: MaquinariaDeInforme) =>
    (filtro === "todas" ||
      (filtro === "sin_valorizar" && fila.valor_comercial_total === null) ||
      (filtro === "sin_validar" && !fila.validada)) &&
    (consulta === "" || normalizar(fila.nombre).includes(consulta));

  return {
    visibles: filas.filter(cumple),
    conteos: {
      todas: filas.length,
      sin_valorizar: filas.filter((f) => f.valor_comercial_total === null).length,
      sin_validar: filas.filter((f) => !f.validada).length,
    },
    resumen: resumenDelCuadro(filas),
  };
}
